import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  CircularProgress,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from '../../contexts/TranslationContext';
import { marketplace, UserIntegrationWithDetails } from '../../services/api';
import {
  buildAidaSettingsPathWithoutFormId,
  buildDidarFormUrlFromId,
  DIDAR_SETTING_FORM_ID_PARAM,
} from './didarSettingRoute';
import ConfirmationModal from './components/ConfirmationModal';

const DIDAR_CRM_INTEGRATION_TITLE = 'didarcrm';

const isDidarCrmIntegration = (integration: { title?: string }): boolean =>
  integration.title?.trim().toLowerCase() === DIDAR_CRM_INTEGRATION_TITLE;

export const userHasActiveDidarIntegration = (
  items: UserIntegrationWithDetails[],
): boolean =>
  items.some((item) => isDidarCrmIntegration(item.integration) && item.is_active);

export const findActiveDidarUserIntegration = (
  items: UserIntegrationWithDetails[],
): UserIntegrationWithDetails | undefined =>
  items.find((item) => isDidarCrmIntegration(item.integration) && item.is_active);

export const USER_INTEGRATIONS_CHANGED_EVENT = 'aida:user-integrations-changed';

export const notifyUserIntegrationsChanged = () => {
  window.dispatchEvent(new CustomEvent(USER_INTEGRATIONS_CHANGED_EVENT));
};

export function useHasActiveDidarIntegration() {
  const [hasDidar, setHasDidar] = useState(false);
  const [didarUserIntegration, setDidarUserIntegration] =
    useState<UserIntegrationWithDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);

  const refetch = useCallback(() => {
    setRefreshKey((key) => key + 1);
  }, []);

  useEffect(() => {
    const onIntegrationsChanged = () => refetch();
    window.addEventListener(USER_INTEGRATIONS_CHANGED_EVENT, onIntegrationsChanged);
    return () => {
      window.removeEventListener(USER_INTEGRATIONS_CHANGED_EVENT, onIntegrationsChanged);
    };
  }, [refetch]);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const response = await marketplace.getUserIntegrations({
          page: 1,
          size: 20,
          is_active: true,
        });
        if (!cancelled) {
          const items = response.data.items;
          setHasDidar(userHasActiveDidarIntegration(items));
          setDidarUserIntegration(findActiveDidarUserIntegration(items) ?? null);
        }
      } catch {
        if (!cancelled) {
          setHasDidar(false);
          setDidarUserIntegration(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void check();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  return { hasDidar, loading, didarUserIntegration };
}

interface DidarAdvancedSettingsProps {
  didarUserIntegration?: UserIntegrationWithDetails | null;
}

interface DidarWebFormField {
  Key: string;
  Title: string;
  ControlType?: string;
  description?: string;
  [key: string]: unknown;
}

/** Full JSON body from GetWebForm (root may include Message, Response, etc.) */
type DidarGetWebFormApiBody = Record<string, unknown>;

const DIDAR_COLLECTION_CONDITIONS_KEY = 'AidaFormCollectionConditions';
/** Tail fields excluded when loading form via manual URL confirm (GetWebForm) */
const DIDAR_FORM_FIELDS_TAIL_EXCLUDE_MANUAL = 3;
/** Tail fields excluded when loading form from saved FormSchema (myIntegration) */
const DIDAR_FORM_FIELDS_TAIL_EXCLUDE_SAVED = 4;

const parseJsonRecord = (value: unknown): DidarGetWebFormApiBody | null => {
  if (value == null || value === '') {
    return null;
  }

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed || trimmed === '""') {
      return null;
    }
    try {
      const parsed: unknown = JSON.parse(trimmed);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return parsed as DidarGetWebFormApiBody;
      }
    } catch {
      return null;
    }
    return null;
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    return value as DidarGetWebFormApiBody;
  }

  return null;
};

const getDidarResponseNode = (apiBody: DidarGetWebFormApiBody): DidarGetWebFormApiBody | null => {
  const wrapped = apiBody.Response ?? apiBody.response;
  const parsedWrapped = parseJsonRecord(wrapped);
  if (parsedWrapped) {
    return parsedWrapped;
  }
  if (Array.isArray(apiBody.Fields) || Array.isArray(apiBody.fields)) {
    return apiBody;
  }
  return null;
};

const getDidarFieldsFromApiBody = (apiBody: DidarGetWebFormApiBody): DidarWebFormField[] => {
  const responseNode = getDidarResponseNode(apiBody);
  if (!responseNode) return [];
  const fields = responseNode.Fields ?? responseNode.fields;
  let rawFields: unknown[] = [];
  if (Array.isArray(fields)) {
    rawFields = fields;
  } else {
    const parsedFields = parseJsonRecord(fields);
    const parsedFieldList = parsedFields?.Fields ?? parsedFields?.fields;
    if (Array.isArray(parsedFieldList)) {
      rawFields = parsedFieldList;
    }
  }

  return rawFields
    .map((field) => {
      if (!field || typeof field !== 'object' || Array.isArray(field)) {
        return null;
      }
      const record = field as Record<string, unknown>;
      const key = record.Key ?? record.key;
      if (typeof key !== 'string' || !key.trim()) {
        return null;
      }
      const title = record.Title ?? record.title;
      const description =
        typeof record.description === 'string'
          ? record.description
          : typeof record.Description === 'string'
            ? record.Description
            : undefined;

      return {
        ...record,
        Key: key,
        Title: typeof title === 'string' ? title : String(title ?? ''),
        description,
      } as DidarWebFormField;
    })
    .filter((field): field is DidarWebFormField => field !== null);
};

const getFormIdFromResponseNode = (responseNode: DidarGetWebFormApiBody): string | null => {
  const rawId = responseNode.Id ?? responseNode.id;
  if (typeof rawId === 'string' && rawId.trim()) {
    return rawId.trim();
  }
  return null;
};

const getFormSchemaFromConfiguration = (
  configuration: DidarGetWebFormApiBody,
): DidarGetWebFormApiBody | null => {
  const formSchemaRaw = configuration.FormSchema ?? configuration.formSchema;
  if (formSchemaRaw == null || formSchemaRaw === '') {
    return null;
  }
  return parseJsonRecord(formSchemaRaw);
};

const getSavedFormIdFromApiBody = (apiBody: DidarGetWebFormApiBody): string | null => {
  const responseNode = getDidarResponseNode(apiBody);
  if (!responseNode) return null;
  return getFormIdFromResponseNode(responseNode);
};

const areDidarFormIdsEqual = (left: string, right: string): boolean =>
  left.trim().toLowerCase() === right.trim().toLowerCase();

const extractSavedDidarFormState = (
  apiBody: DidarGetWebFormApiBody,
): {
  formId: string;
  fields: DidarWebFormField[];
  fieldDescriptions: Record<string, string>;
  supplementaryNotes: string;
} | null => {
  const responseNode = getDidarResponseNode(apiBody);
  if (!responseNode) return null;

  const formId = getFormIdFromResponseNode(responseNode);
  if (!formId) return null;

  const allFields = getDidarFieldsFromApiBody(apiBody);
  const collectionField = allFields.find(
    (field) => field.Key === DIDAR_COLLECTION_CONDITIONS_KEY,
  );
  const supplementaryNotes =
    typeof collectionField?.description === 'string'
      ? collectionField.description
      : '';

  const fields = allFields
    .slice(0, Math.max(0, allFields.length - DIDAR_FORM_FIELDS_TAIL_EXCLUDE_SAVED))
    .filter((field) => field.Key !== DIDAR_COLLECTION_CONDITIONS_KEY);
  if (fields.length === 0) return null;

  return {
    formId,
    fields,
    fieldDescriptions: Object.fromEntries(
      fields.map((field) => [
        field.Key,
        typeof field.description === 'string' ? field.description : '',
      ]),
    ),
    supplementaryNotes,
  };
};

const setDidarFieldsInApiBody = (
  apiBody: DidarGetWebFormApiBody,
  fields: DidarWebFormField[],
): void => {
  const responseNode = getDidarResponseNode(apiBody);
  if (!responseNode) return;
  responseNode.Fields = fields;
};

const DIDAR_GET_WEB_FORM_URL = 'https://app.didar.me/api/webform/GetWebForm';
const DIDAR_ACCENT_COLOR = '#009688';
const DIDAR_ACCENT_HOVER = '#00796b';
const DIDAR_FORM_MAX_WIDTH = 640;
const DIDAR_PAGE_MAX_WIDTH = 1120;

const DidarAdvancedHelpSection: FC = () => {
  const { t, tArray } = useTranslation();
  const helpKey = 'marketplace.connections.didarAdvanced.help';
  const fieldPurposeGoals = tArray(`${helpKey}.fieldPurposeGoals`);
  const displayExamples = tArray(`${helpKey}.displayExamples`);

  const bodyTextSx = { color: 'text.secondary', lineHeight: 1.75, mb: 1.5 };
  const sectionTitleSx = { fontWeight: 600, mb: 1, mt: 2.5 };

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, sm: 2.5 },
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, fontSize: { xs: '1rem', md: '1.125rem' } }}>
        {t(`${helpKey}.title`)}
      </Typography>

      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {t(`${helpKey}.pageSetupTitle`)}
      </Typography>
      <Typography variant="body2" sx={bodyTextSx}>
        {t(`${helpKey}.pageSetupP1`)}
      </Typography>
      <Typography variant="body2" sx={bodyTextSx}>
        {t(`${helpKey}.pageSetupP2`)}
      </Typography>
      <Typography variant="body2" sx={bodyTextSx}>
        {t(`${helpKey}.pageSetupP3`)}
      </Typography>

      <Typography variant="body2" sx={{ ...bodyTextSx, mb: 0.5 }}>
        {t(`${helpKey}.fieldPurposeIntro`)}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1.5 }}>
        {fieldPurposeGoals.map((item) => (
          <Typography key={item} component="li" variant="body2" sx={bodyTextSx}>
            {item}
          </Typography>
        ))}
      </Box>
      <Typography variant="body2" sx={bodyTextSx}>
        {t(`${helpKey}.fieldPurposeClosing`)}
      </Typography>

      <Typography variant="subtitle2" sx={sectionTitleSx}>
        {t(`${helpKey}.exampleTitle`)}
      </Typography>
      <Typography variant="body2" sx={bodyTextSx}>
        {t(`${helpKey}.exampleCompany`)}
      </Typography>
      <Typography variant="body2" sx={bodyTextSx}>
        {t(`${helpKey}.examplePhone`)}
      </Typography>

      <Typography variant="subtitle2" sx={sectionTitleSx}>
        {t(`${helpKey}.displayConditionsTitle`)}
      </Typography>
      <Typography variant="body2" sx={bodyTextSx}>
        {t(`${helpKey}.displayConditionsP1`)}
      </Typography>
      <Typography variant="body2" sx={bodyTextSx}>
        {t(`${helpKey}.displayConditionsP2`)}
      </Typography>

      <Typography variant="subtitle2" sx={{ ...sectionTitleSx, mt: 1.5 }}>
        {t(`${helpKey}.displayExamplesTitle`)}
      </Typography>
      <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 1.5 }}>
        {displayExamples.map((item) => (
          <Typography key={item} component="li" variant="body2" sx={bodyTextSx}>
            {item}
          </Typography>
        ))}
      </Box>
      <Typography variant="body2" sx={{ ...bodyTextSx, mb: 0 }}>
        {t(`${helpKey}.displayConditionsClosing`)}
      </Typography>
    </Paper>
  );
};

const extractDidarFormId = (input: string): string | null => {
  const trimmed = input.trim();
  const fromUrl = trimmed.match(/\/form\/([^/?#]+)/i);
  if (fromUrl?.[1]) return fromUrl[1];
  const uuidOnly = trimmed.match(
    /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i,
  );
  if (uuidOnly) return trimmed;
  return null;
};

const DidarAdvancedSettings: FC<DidarAdvancedSettingsProps> = ({ didarUserIntegration }) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const inputSize = isMobile ? 'small' : 'medium';
  const autoLoadFromUrlRef = useRef(false);
  const initialFormLoadDoneRef = useRef(false);
  const pendingSavedApiBodyRef = useRef<DidarGetWebFormApiBody | null>(null);
  const skipModalCancelCleanupRef = useRef(false);
  const [showNewFormConfirmModal, setShowNewFormConfirmModal] = useState(false);
  const [pendingRoutingFormId, setPendingRoutingFormId] = useState<string | null>(null);
  const [didarFormUrl, setDidarFormUrl] = useState('');
  const didarFormIdRef = useRef<string | null>(null);
  const [didarFormFields, setDidarFormFields] = useState<DidarWebFormField[]>([]);
  const [didarFieldDescriptions, setDidarFieldDescriptions] = useState<Record<string, string>>({});
  const [didarFormLoaded, setDidarFormLoaded] = useState(false);
  const [didarFormLoading, setDidarFormLoading] = useState(false);
  const [didarFormError, setDidarFormError] = useState<string | null>(null);
  const [didarSupplementaryNotes, setDidarSupplementaryNotes] = useState('');
  const [didarFullApiBody, setDidarFullApiBody] = useState<DidarGetWebFormApiBody | null>(null);
  const [didarSubmitError, setDidarSubmitError] = useState<string | null>(null);
  const [didarSubmitting, setDidarSubmitting] = useState(false);

  const isDidarFormComplete =
    didarFormLoaded &&
    didarFormFields.length > 0 &&
    didarFormFields.every((field) => (didarFieldDescriptions[field.Key] ?? '').trim().length > 0) &&
    didarSupplementaryNotes.trim().length > 0;

  const buildDidarFormPayload = (): DidarGetWebFormApiBody | null => {
    if (!didarFullApiBody) {
      return null;
    }

    const payload = structuredClone(didarFullApiBody) as DidarGetWebFormApiBody;
    const existingFields = getDidarFieldsFromApiBody(payload);
    if (existingFields.length === 0) {
      return payload;
    }

    const collectionFieldIndex = existingFields.findIndex(
      (field) => field.Key === DIDAR_COLLECTION_CONDITIONS_KEY,
    );
    const hasExistingCollectionField = collectionFieldIndex >= 0;

    const fieldsWithDescriptions = existingFields.map((field) => {
      if (field.Key === DIDAR_COLLECTION_CONDITIONS_KEY) {
        return {
          ...field,
          description: didarSupplementaryNotes.trim(),
        };
      }

      const userDescription = didarFieldDescriptions[field.Key];
      if (userDescription === undefined) {
        return field;
      }
      return {
        ...field,
        description: userDescription.trim(),
      };
    });

    if (hasExistingCollectionField) {
      setDidarFieldsInApiBody(payload, fieldsWithDescriptions);
      return payload;
    }

    const collectionConditionsField: DidarWebFormField = {
      Key: DIDAR_COLLECTION_CONDITIONS_KEY,
      Title: t('marketplace.connections.didarAdvanced.supplementaryLabel'),
      ControlType: 'TextArea',
      _block: 'conditions',
      description: didarSupplementaryNotes.trim(),
    };

    setDidarFieldsInApiBody(payload, [...fieldsWithDescriptions, collectionConditionsField]);
    return payload;
  };

  const handleDidarFormSubmit = async () => {
    if (!isDidarFormComplete) {
      setDidarSubmitError(t('marketplace.connections.didarAdvanced.submitIncomplete'));
      return;
    }

    if (!didarUserIntegration?.id) {
      setDidarSubmitError(t('marketplace.connections.didarAdvanced.submitNoIntegration'));
      return;
    }

    const formSchema = buildDidarFormPayload();
    if (!formSchema) {
      setDidarSubmitError(t('chatbox.didarForm.loadError'));
      return;
    }

    const existingConfiguration =
      didarUserIntegration.configuration &&
      typeof didarUserIntegration.configuration === 'object' &&
      !Array.isArray(didarUserIntegration.configuration)
        ? didarUserIntegration.configuration
        : {};

    setDidarSubmitting(true);
    setDidarSubmitError(null);

    try {
      await marketplace.setupIntegration(didarUserIntegration.id, {
        configuration: {
          ...existingConfiguration,
          FormSchema: formSchema,
        },
        notes: didarUserIntegration.notes,
      });

      enqueueSnackbar(t('marketplace.myIntegrations.configurationSaved'), {
        variant: 'success',
        autoHideDuration: 2000,
        style: { padding: '10px', marginTop: '20px' },
      });
      notifyUserIntegrationsChanged();
    } catch (error: unknown) {
      const message =
        (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail ??
        t('marketplace.myIntegrations.configurationSaveError');
      setDidarSubmitError(message);
      enqueueSnackbar(message, {
        variant: 'error',
        autoHideDuration: 2000,
        style: { padding: '10px', marginTop: '20px' },
      });
    } finally {
      setDidarSubmitting(false);
    }
  };

  const handleDidarFormUrlConfirm = useCallback(
    async (urlOverride?: string) => {
      const urlToUse = (urlOverride ?? didarFormUrl).trim();
      const formId = extractDidarFormId(urlToUse);
      if (!formId) {
        setDidarFormError(t('chatbox.didarForm.invalidUrl'));
        setDidarFormLoaded(false);
        setDidarFormFields([]);
        setDidarFullApiBody(null);
        return;
      }

      setDidarFormLoading(true);
      setDidarFormError(null);
      setDidarSubmitError(null);
      setDidarFullApiBody(null);

      try {
        const response = await fetch(DIDAR_GET_WEB_FORM_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ Id: formId }),
        });

        if (!response.ok) {
          throw new Error('request_failed');
        }

        const rawText = await response.text();
        const apiBody = JSON.parse(rawText) as DidarGetWebFormApiBody;
        setDidarFullApiBody(apiBody);

        const allFields = getDidarFieldsFromApiBody(apiBody);
        const fields = allFields.slice(
          0,
          Math.max(0, allFields.length - DIDAR_FORM_FIELDS_TAIL_EXCLUDE_MANUAL),
        );

        if (fields.length === 0) {
          throw new Error('empty_fields');
        }

        didarFormIdRef.current = formId;
        setDidarFormFields(fields);
        setDidarFieldDescriptions(
          Object.fromEntries(fields.map((field) => [field.Key, ''])),
        );
        setDidarSupplementaryNotes('');
        setDidarFormLoaded(true);
      } catch {
        setDidarFormError(t('chatbox.didarForm.loadError'));
        setDidarFormLoaded(false);
        setDidarFormFields([]);
        didarFormIdRef.current = null;
        setDidarFullApiBody(null);
      } finally {
        setDidarFormLoading(false);
      }
    },
    [didarFormUrl, t],
  );

  const applySavedDidarFormState = useCallback(
    (apiBody: DidarGetWebFormApiBody) => {
      const saved = extractSavedDidarFormState(apiBody);
      if (!saved) return false;

      didarFormIdRef.current = saved.formId;
      setDidarFormUrl(buildDidarFormUrlFromId(saved.formId));
      setDidarFullApiBody(apiBody);
      setDidarFormFields(saved.fields);
      setDidarFieldDescriptions(saved.fieldDescriptions);
      setDidarSupplementaryNotes(saved.supplementaryNotes);
      setDidarFormLoaded(true);
      setDidarFormError(null);
      return true;
    },
    [],
  );

  useEffect(() => {
    if (initialFormLoadDoneRef.current) {
      return;
    }

    const formIdFromUrl = searchParams.get(DIDAR_SETTING_FORM_ID_PARAM)?.trim();
    const integrationId = didarUserIntegration?.id;

    const resolveFormSchemaFromConfiguration = (
      configurationRaw: unknown,
    ): DidarGetWebFormApiBody | null => {
      const configuration = parseJsonRecord(configurationRaw);
      if (!configuration) return null;
      return getFormSchemaFromConfiguration(configuration);
    };

    let cancelled = false;

    const runInitialLoad = async () => {
      let savedApiBody: DidarGetWebFormApiBody | null = null;

      if (integrationId && didarUserIntegration) {
        savedApiBody = resolveFormSchemaFromConfiguration(didarUserIntegration.configuration);

        if (!savedApiBody) {
          try {
            const response = await marketplace.getUserIntegrationDetail(integrationId);
            if (cancelled) return;
            savedApiBody = resolveFormSchemaFromConfiguration(response.data.configuration);
          } catch {
            // Continue without saved FormSchema.
          }
        }
      }

      if (cancelled) return;

      const savedFormId = savedApiBody ? getSavedFormIdFromApiBody(savedApiBody) : null;

      if (formIdFromUrl) {
        if (savedFormId && savedApiBody && !areDidarFormIdsEqual(formIdFromUrl, savedFormId)) {
          pendingSavedApiBodyRef.current = savedApiBody;
          setPendingRoutingFormId(formIdFromUrl);
          setShowNewFormConfirmModal(true);
          initialFormLoadDoneRef.current = true;
          return;
        }

        if (savedApiBody && savedFormId && areDidarFormIdsEqual(formIdFromUrl, savedFormId)) {
          applySavedDidarFormState(savedApiBody);
          initialFormLoadDoneRef.current = true;
          return;
        }

        if (!autoLoadFromUrlRef.current) {
          autoLoadFromUrlRef.current = true;
          const fullUrl = buildDidarFormUrlFromId(formIdFromUrl);
          setDidarFormUrl(fullUrl);
          void handleDidarFormUrlConfirm(fullUrl);
        }

        initialFormLoadDoneRef.current = true;
        return;
      }

      if (savedApiBody && applySavedDidarFormState(savedApiBody)) {
        initialFormLoadDoneRef.current = true;
        return;
      }

      initialFormLoadDoneRef.current = true;
    };

    void runInitialLoad();

    return () => {
      cancelled = true;
    };
  }, [searchParams, didarUserIntegration, applySavedDidarFormState, handleDidarFormUrlConfirm]);

  const handleConfirmNewRoutingForm = () => {
    skipModalCancelCleanupRef.current = true;

    const formId = pendingRoutingFormId;
    setShowNewFormConfirmModal(false);
    setPendingRoutingFormId(null);
    pendingSavedApiBodyRef.current = null;

    if (!formId) return;

    autoLoadFromUrlRef.current = true;
    const fullUrl = buildDidarFormUrlFromId(formId);
    setDidarFormUrl(fullUrl);
    void handleDidarFormUrlConfirm(fullUrl);
  };

  const handleCancelNewRoutingForm = () => {
    setShowNewFormConfirmModal(false);
    setPendingRoutingFormId(null);

    if (skipModalCancelCleanupRef.current) {
      skipModalCancelCleanupRef.current = false;
      return;
    }

    const apiBody = pendingSavedApiBodyRef.current;
    pendingSavedApiBodyRef.current = null;

    if (apiBody) {
      applySavedDidarFormState(apiBody);
    }

    if (searchParams.get(DIDAR_SETTING_FORM_ID_PARAM)?.trim()) {
      navigate(buildAidaSettingsPathWithoutFormId(searchParams), { replace: true });
    }
  };

  const handleDidarFieldDescriptionChange = (key: string, value: string) => {
    setDidarFieldDescriptions((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        py: 2,
        px: { xs: 1, sm: 2 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'flex-start',
          gap: { xs: 3, md: 10 },
          width: '100%',
          maxWidth: DIDAR_PAGE_MAX_WIDTH,
          mx: 'auto',
        }}
      >
        <Box
          sx={{
            flex: { md: '1 1 0' },
            minWidth: 0,
            width: '100%',
            maxWidth: { md: DIDAR_FORM_MAX_WIDTH },
          }}
        >
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{ mb: 3, textAlign: 'center', fontSize: { xs: '1rem', md: '1.25rem' } }}
        >
          {t('chatbox.didarForm.title')}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center' , justifyContent: 'center', gap: 1.5, mb: 2 }}>
          <TextField
            fullWidth
            size={inputSize}
            variant="outlined"
            placeholder={t('chatbox.didarForm.urlPlaceholder')}
            value={didarFormUrl}
            onChange={(event) => {
              setDidarFormUrl(event.target.value);
              if (didarFormError) setDidarFormError(null);
              setDidarSubmitError(null);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !didarFormLoading) {
                void handleDidarFormUrlConfirm();
              }
            }}
            disabled={didarFormLoading}
            error={Boolean(didarFormError && !didarFormLoaded)}
            helperText={!didarFormLoaded ? didarFormError ?? undefined : undefined}
          />
          <IconButton
            onClick={() => void handleDidarFormUrlConfirm()}
            disabled={didarFormLoading || !didarFormUrl.trim()}
            sx={{
              mt: { xs: 0.25, sm: 0.5 },
              flexShrink: 0,
              width: { xs: 36, sm: 48 },
              height: { xs: 36, sm: 48 },
              backgroundColor: DIDAR_ACCENT_COLOR,
              color: '#fff',
              borderRadius: '50%',
              '& .MuiSvgIcon-root': {
                fontSize: { xs: 20, sm: 24 },
              },
              '&:hover': {
                backgroundColor: DIDAR_ACCENT_HOVER,
              },
              '&.Mui-disabled': {
                backgroundColor: 'action.disabledBackground',
                color: 'action.disabled',
              },
            }}
            aria-label={t('chatbox.didarForm.confirmUrl')}
          >
            {didarFormLoading ? (
              <CircularProgress
                sx={{
                  color: '#fff',
                  width: { xs: 20, sm: 24 },
                  height: { xs: 20, sm: 24 },
                }}
              />
            ) : (
              <CheckIcon />
            )}
          </IconButton>
        </Box>

        {didarFormLoaded && didarFormFields.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 3 }}>
            {didarFormFields.map((field) => (
              <Box
                key={field.Key}
                component="fieldset"
                sx={{ border: 'none', m: 0, p: 0, minWidth: 0 }}
              >
                <Typography
                  component="legend"
                  variant="body2"
                  fontWeight={600}
                  sx={{ mb: 1, float: 'none', width: 'auto', px: 0 }}
                >
                  {t('chatbox.didarForm.fieldTitle', { title: field.Title })}
                </Typography>
                <TextField
                  fullWidth
                  size={inputSize}
                  multiline
                  minRows={1}
                  variant="outlined"
                  label={t('chatbox.didarForm.fieldLegend', { title: field.Title })}
                  placeholder={t('chatbox.didarForm.fieldPlaceholder', { title: field.Title })}
                  value={didarFieldDescriptions[field.Key] ?? ''}
                  onChange={(event) => {
                    handleDidarFieldDescriptionChange(field.Key, event.target.value);
                    if (didarSubmitError) setDidarSubmitError(null);
                  }}
                />
              </Box>
            ))}

            <Box
              component="fieldset"
              sx={{ border: 'none', m: 0, p: 0, minWidth: 0 }}
            >
              <Typography
                component="legend"
                variant="body2"
                fontWeight={600}
                sx={{ mb: 1, float: 'none', width: 'auto', px: 0 }}
              >
                {t('marketplace.connections.didarAdvanced.supplementaryLabel')}
              </Typography>
              <TextField
                fullWidth
                size={inputSize}
                multiline
                minRows={isMobile ? 3 : 4}
                variant="outlined"
                label={t('marketplace.connections.didarAdvanced.supplementaryFieldLegend')}
                placeholder={t('marketplace.connections.didarAdvanced.supplementaryPlaceholder')}
                value={didarSupplementaryNotes}
                onChange={(event) => {
                  setDidarSupplementaryNotes(event.target.value);
                  if (didarSubmitError) setDidarSubmitError(null);
                }}
              />
            </Box>

            {didarSubmitError && (
              <Typography variant="body2" color="error" sx={{ mt: -1 }}>
                {didarSubmitError}
              </Typography>
            )}

            <Button
              fullWidth
              variant="contained"
              onClick={() => void handleDidarFormSubmit()}
              disabled={!isDidarFormComplete || didarSubmitting || !didarUserIntegration?.id}
              sx={{
                py: 1.25,
                backgroundColor: DIDAR_ACCENT_COLOR,
                color: '#fff',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: DIDAR_ACCENT_HOVER,
                },
              }}
            >
              {didarSubmitting ? (
                <CircularProgress size={22} sx={{ color: '#fff' }} />
              ) : (
                t('chatbox.didarForm.confirm')
              )}
            </Button>
          </Box>
        )}
        </Box> 

        <Box
          sx={{
            flex: { md: '1 1 0' },
            minWidth: 0,
            width: '100%',
          }}
        >
          <DidarAdvancedHelpSection />
        </Box>
      </Box>

      <ConfirmationModal
        isOpen={showNewFormConfirmModal}
        onClose={handleCancelNewRoutingForm}
        onConfirm={handleConfirmNewRoutingForm}
        title={t('marketplace.connections.didarAdvanced.newFormConfirmTitle')}
        message={t('marketplace.connections.didarAdvanced.newFormConfirmMessage')}
        confirmText={t('marketplace.connections.didarAdvanced.newFormConfirmYes')}
        cancelText={t('marketplace.connections.didarAdvanced.newFormConfirmCancel')}
        confirmButtonClass="btn-primary"
      />
    </Box>
  );
};

export default DidarAdvancedSettings;
